'use client'
import React, { useEffect, useRef, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { LinkedinIcons } from './chat-icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ResizableTextarea from "react-textarea-autosize";

import Image from 'next/image';
import './linkedin-chat.css';
import { useTranslations } from 'next-intl';
import { renderTextWithLinks } from '@/lib/linkify';

type Author = 'contact' | 'me';

type ChatMessage = {
  id: string;
  text: string;
  author: Author;
  source: string;
  sending: boolean;
  sent_at: string; // ISO timestamp
  attachments: unknown[];
  isTyping?: boolean;
};

interface LinkedinChatProps {
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  onSend: () => void;
}

const formatTime = (iso: string) => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    return formatInTimeZone(new Date(iso), tz, 'HH:mm');
  } catch {
    return '';
  }
};



const formatDate = (iso: string, tCommon: (key: string) => string) => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const d = new Date(iso);
    const today = new Date();

    const msgDay = formatInTimeZone(d, tz, 'yyyy-MM-dd');
    const todayDay = formatInTimeZone(today, tz, 'yyyy-MM-dd');
    const yestDay = formatInTimeZone(new Date(today.getTime() - 24 * 60 * 60 * 1000), tz, 'yyyy-MM-dd');

    if (msgDay === todayDay) return tCommon('today');
    if (msgDay === yestDay) return tCommon('yesterday');

    return formatInTimeZone(d, tz, 'MMM d').toUpperCase();
  } catch {
    return '';
  }
};

const isSameDay = (date1: string, date2: string) => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const day1 = formatInTimeZone(d1, tz, 'yyyy-MM-dd');
    const day2 = formatInTimeZone(d2, tz, 'yyyy-MM-dd');
    return day1 === day2;
  } catch {
    return false;
  }
};

const LinkedInMessagesView = React.memo(function LinkedInMessagesView({ messages, containerRef, me, contact }: { messages: ChatMessage[]; containerRef: React.RefObject<HTMLDivElement | null>; me: { name: string; avatar: string; bio: string }, contact: { name: string; avatar: string } }) {
  const tCommon = useTranslations('chat.common');
  const tLn = useTranslations('chat.linkedin');

  const prevMessageCount = useRef(messages.length)
  const animatedIdsRef = useRef<Set<string>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (containerRef.current && messages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
    prevMessageCount.current = messages.length
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (containerRef.current && messages.length > prevMessageCount.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
    prevMessageCount.current = messages.length
  }, [messages, containerRef])

  useEffect(() => {
    if (!containerRef.current) return
    if (observerRef.current) {
      observerRef.current.disconnect()
    }
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target as HTMLElement
        const id = el.dataset.messageId
        if (id && !animatedIdsRef.current.has(id)) {
          animatedIdsRef.current.add(id)
          el.classList.add('linkedin-message-animate')
          observerRef.current?.unobserve(el)
        }
      })
    }, { root: containerRef.current, threshold: 0.1 })

    return () => observerRef.current?.disconnect()
  }, [messages.length, containerRef])

  const setMessageEl = (id: string) => (el: HTMLDivElement | null) => {
    if (!el) return
    el.dataset.messageId = id
    if (!animatedIdsRef.current.has(id)) {
      observerRef.current?.observe(el)
    }
  }

  return (
    <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y py-4 pb-6 space-y-4 linkedin-chat-container">
      <div className="px-4 ">
        <div className="break-words">
          <div className="block -translate-x-1">
            <div className="relative flex size-18">
              <Avatar className="size-full" >
                <AvatarImage src={me.avatar} />
                <AvatarFallback>
                  {me.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="size-4 rounded-full bg-green-600 shrin-0 box-border absolute bottom-0.5 right-0.5 shadow-[0_0_0_2px_#FFF]"></div>
            </div>
          </div>
          <div className="pt-2 pl-2 overflow-hidden">
            <div className="font-semibold text-sm leading-[1.5] truncate flex text-gray-1000">
              <span className="truncate flex items-center mr-1">
                <div className="margin-[0_auto] truncate flex justify-center items-center">
                  <span className="truncate">{me.name}</span>
                  <Image src={'/ln-premium.svg'} alt={tLn('premiumAlt')} width={14} height={14} className="size-3.5 shrink-0 ml-1" />
                </div>
              </span>
              <div className="text-sm text-gray-1000/60 inline-flex items-center relative">
                <span className="">{tLn('degree')}</span>
              </div>
            </div>
            <div className="overflow-hidden text-ellipsis m-[0_auto] max-h-10 text-sm">
              <div>{me.bio}</div>
              <div><div></div></div>
            </div>
          </div>
        </div>
      </div>

      {messages.map((msg, index) => {
        const isMe = msg.author === 'contact';
        const senderName = isMe ? contact.name : me.name;
        const avatar = isMe ? contact.avatar : me.avatar;
        const prevMsg = index > 0 ? messages[index - 1] : null;
        const showDateSeparator = index === 0 || (prevMsg && !isSameDay(prevMsg.sent_at, msg.sent_at));
        if (msg.isTyping && !msg.text) return null;
        return (
          <React.Fragment key={msg.id}>
            {showDateSeparator && (
              <div className="text-center text-xs flex items-center tracking-[1px] my-2 uppercase text-gray-500 before:content-[''] before:block before:grow before:mr-3 before:border-t before:border-gray-200 after:content-[''] after:block after:grow after:ml-3 after:border-t after:border-gray-200 animate-fadeIn" suppressHydrationWarning>
                {formatDate(msg.sent_at, tCommon)}
              </div>
            )}
            <div ref={setMessageEl(msg.id)} data-message-id={msg.id} className={`flex items-start pl-1 pr-12 ${index === 0 || (prevMsg && prevMsg.author !== msg.author) || showDateSeparator ? 'mt-4' : 'mt-1'} ${isMe ? 'from-contact' : 'from-me'}`}>
              {(index === 0 || (prevMsg && prevMsg.author !== msg.author) || showDateSeparator) ? (
                <Image src={avatar} alt={senderName} width={40} height={40} className="rounded-full object-cover" sizes="40px" />
              ) : (
                <div className="w-10 shrink-0" />
              )}
              <div className="ml-3 min-w-0">
                {(index === 0 || (prevMsg && prevMsg.author !== msg.author) || showDateSeparator) && (
                  <div className="flex items-center mb-1">
                    <span className="font-medium text-sm">{senderName}</span>
                    <span className="ml-2 text-xs text-gray-500" suppressHydrationWarning>· {formatTime(msg.sent_at)}</span>
                  </div>
                )}
                {index === 0 && (
                  <div className="mt-2 mb-3">
                    <span className="flex text-xs font-medium ">
                      <div className="mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg"  className="size-4 text-[#F8C19E]" viewBox="0 0 16 16"  fill="currentColor" width="16" height="16" focusable="false">
                          <path d="M5.11 5.35L11.36 8l-6.24 2.64L6 8l-.89-2.65M2 2l2 6-2 6 12.56-5.33a.75.75 0 000-1.37L2 2z"></path>
                        </svg>
                      </div>
                      <span className="text-gray-1000/60">{tLn('invitedYou')}</span>
                    </span>
                  </div>
                )}
                <p className="text-sm text-gray-800 whitespace-pre-wrap [overflow-wrap:anywhere]">{renderTextWithLinks(msg.text)}</p>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {messages.some(m => m.isTyping && !m.text) && (
        <div className="flex gap-1 py-1 justify-start pl-4 animate-fadeInUp">
          <Avatar className="size-4" >
            <AvatarImage src={me.avatar} />
            <AvatarFallback>
              {me.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-0.5">
            <span className="linkedin-typing-dot"></span>
            <span className="linkedin-typing-dot" style={{ animationDelay: '0.2s' }}></span>
            <span className="linkedin-typing-dot" style={{ animationDelay: '0.4s' }}></span>
          </div>
        </div>
      )}
    </div>
  )
}, (prev, next) => prev.messages === next.messages)

export const LinkedInChat = ({ messages, input, setInput, onSend }: LinkedinChatProps) => {
  const tCommon = useTranslations('chat.common');
  const tLn = useTranslations('chat.linkedin');
  const contact = {
    name: tLn('you'),
    avatar: '/linkedin-profile.svg',
  };

  const me = {
    name: tLn('me.name'),
    avatar: '/agent/ramacastro-144.avif',
    bio: tLn('me.bio')
  };


  function TypingIndicator() {
    return (
      <div className="flex gap-1 py-1 justify-start pl-4 animate-fadeInUp">
        <Avatar className="size-4" >
          <AvatarImage src={me.avatar} />
          <AvatarFallback>
            {me.name.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-0.5">
          <span className="linkedin-typing-dot"></span>
          <span className="linkedin-typing-dot" style={{ animationDelay: '0.2s' }}></span>
          <span className="linkedin-typing-dot" style={{ animationDelay: '0.4s' }}></span>
        </div>
      </div>
    );
  }


  const [isTextAreaFull, setIsTextAreaFull] = useState(false);

  const chatContainerRef = useRef<HTMLDivElement>(null)

  // Message list is memoized; no per-message observers needed here

  const handleFocus = () => {
    if (chatContainerRef.current) {
      setTimeout(() => {
        chatContainerRef.current!.scrollTop = chatContainerRef.current!.scrollHeight
      }, 50)
    }
  }

  return (
    <div className="flex size-full bg-white text-gray-900 p-1">
      <div className="mx-auto bg-white h-full flex flex-col min-h-0 w-full">
        {/* Header */}
        <div className="px-2 border-b border-gray-200 flex flex-row items-center relative min-h-12">


          <div className="flex grow items-center overflow-hidden">

            <div className="py-1">
              <div className="relative">
                <Avatar className="size-8" >
                  <AvatarImage src={me.avatar} />
                  <AvatarFallback>
                    {me.name.charAt(0)}
                  </AvatarFallback>

                </Avatar>
                <div className="size-2 rounded-full bg-green-600 shrin-0 box-border absolute bottom-0 right-0 shadow-[0_0_0_2px_#FFF]"></div>
              </div>



            </div>

            <div className="overflow-hidden grow pl-2">
              <h2 className="truncate pr-1 pl-1 text-sm font-semibold text-gray-1000">
                {me.name}
              </h2>
              <div className="truncate pl-1 text-xs leading-[1.33333] text-gray-1000/90">
                {tCommon('activeNow')}
              </div>

            </div>


          </div>

          <div className="fflex items-center ml-auto shrink-0 relative">
            <div className="size-8 flex items-center justify-center">
              <LinkedinIcons.overflow className="size-4" />
            </div>
          </div>


        </div>

        {/* Messages */}
        <LinkedInMessagesView messages={messages} containerRef={chatContainerRef} me={me} contact={contact} />

        {/* Input Area */}
        <div className="border-t border-gray-200">
          <div className="p-3 flex gap-2">
            <ResizableTextarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={handleFocus}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              placeholder={tLn('placeholder')}
              className="p-3 text-gray-1000 w-full resize-none min-h-15 bg-[#f4f2ee] rounded-lg outline-none max-h-36 text-[16px]"
            />
            <button
              type="button"
              aria-label={tCommon('send')}
              className="size-8 flex items-center justify-center"
            >
              <LinkedinIcons.chevronUpSmall className="size-4" aria-hidden="true" />
            </button>
          </div>

          <div className="flex justify-between items-center py-2.5 px-3">
            <div className="flex space-x-4 text-gray-500 items-center">
              <LinkedinIcons.image className="w-4 h-4" />
              <LinkedinIcons.attachment className="w-4 h-4" />
              <LinkedinIcons.gif className="w-4 h-4" />
              <LinkedinIcons.emoji className="w-4 h-4" />
            </div>
            <div className="flex items-center">
              <button onClick={onSend} className="py-0.5 min-h-6 px-3 rounded-full font-semibold text-sm bg-[#0a66c2] text-white transition-all duration-200 hover:bg-[#004182] active:scale-95">
                {tCommon('send')}
              </button>
              <LinkedinIcons.overflow className="ml-2 w-4 h-4" />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

